import * as React from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import MuiTooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import PaymentIcon from '@mui/icons-material/Payment';
import DownloadIcon from '@mui/icons-material/Download';
import PieChartIcon from '@mui/icons-material/PieChart';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  deleteOne as deleteEmployee,
  getOne as getEmployee,
  getEmployeeStatistics,
  type Employee,
  type EmployeeStatistics,
} from '../data/employees';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import {ArrowForward} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import EmployeeOrdersTable from './EmployeeOrdersTable';
import EmployeeInstallmentsTable from './EmployeeInstallmentsTable';
import DateRangeFilter from './DateRangeFilter';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import {useAuth} from "../../contexts/AuthContext";
import FaceIdSettings from "../../components/FaceIdSettings";

export default function EmployeeShow() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, direction } = useLanguage();
  const { user } = useAuth();
  const dialogs = useDialogs();
  const notifications = useNotification();

  const [employee, setEmployee] = React.useState<Employee | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [activeTab, setActiveTab] = React.useState(() => {
    const state = location.state as { activeTab?: number } | null;
    return state?.activeTab ?? 0;
  });
  const [statistics, setStatistics] = React.useState<EmployeeStatistics | null>(null);
  const [statisticsLoading, setStatisticsLoading] = React.useState(false);
  const [statisticsError, setStatisticsError] = React.useState<Error | null>(null);
  const [dateFrom, setDateFrom] = React.useState<string | null>(null);
  const [dateTo, setDateTo] = React.useState<string | null>(null);

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const showData = await getEmployee(Number(employeeId));

      setEmployee(showData);
    } catch (showDataError) {
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [employeeId]);

  const loadStatistics = React.useCallback(async () => {
    if (!employeeId) return;

    setStatisticsError(null);
    setStatisticsLoading(true);

    try {
      const statsData = await getEmployeeStatistics(Number(employeeId), dateFrom, dateTo);
      setStatistics(statsData);
    } catch (statsError) {
      setStatisticsError(statsError as Error);
    }
    setStatisticsLoading(false);
  }, [employeeId, dateFrom, dateTo]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    const state = location.state as { activeTab?: number } | null;
    if (state?.activeTab !== undefined) {
      setActiveTab(state.activeTab);
      // Clear the state after using it
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  React.useEffect(() => {
    if (activeTab === 3) {
      loadStatistics();
    }
  }, [activeTab, loadStatistics]);

  React.useEffect(() => {
    if (activeTab === 3) {
      loadStatistics();
    }
  }, [dateFrom, dateTo]);

  const handleEmployeeEdit = React.useCallback(() => {
    navigate(`/employees/${employeeId}/edit`);
  }, [navigate, employeeId]);

  const handleEmployeeDelete = React.useCallback(async () => {
    if (!employee) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `${t('messages.deleteConfirm')}`,
      {
        title: t('employees.delete'),
        severity: 'error',
        okText: t('common.delete'),
        cancelText: t('common.cancel'),
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        await deleteEmployee(Number(employeeId));

        navigate('/employees');

        notifications.show(t('messages.deleteSuccess'), {
          severity: 'success',
          autoHideDuration: 3000,
        });
      } catch (deleteError) {
        notifications.show(
          `${t('messages.errorOccurred')}: ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [employee, dialogs, employeeId, navigate, notifications, t]);

  const handleBack = React.useCallback(() => {
    navigate('/employees');
  }, [navigate]);

  const handleContractDownload = React.useCallback(() => {
    if (!employee?.contract_photo_path) {
      return;
    }

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = employee.contract_photo_path;
    link.download = `contract_${employee.name}_${employee.id}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  }, [employee, notifications, t]);

  const handleInstallmentDataChange = React.useCallback(() => {
    // Refresh statistics when installment data changes
    // This ensures statistics are up-to-date when user switches to statistics tab
    loadStatistics();
  }, [loadStatistics]);

  const handleTabChange = React.useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const renderPersonalInfo = React.useMemo(() => {
    if (!employee) return null;

    return (
      <Grid container spacing={3} sx={{ width: '100%', mt: 1 }}>
        {/* Profile Photo Section */}
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 3,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Box sx={{ position: 'relative', width: 150, height: 150 }}>
              {employee.profile_photo_path ? (
                <Avatar
                  src={employee.profile_photo_path}
                  alt={employee.name}
                  sx={{
                    width: 150,
                    height: 150,
                    border: '4px solid',
                    borderColor: '#028bcb',
                    boxShadow: '0 8px 24px rgba(2, 139, 203, 0.25)',
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 150,
                    height: 150,
                    bgcolor: '#028bcb',
                    border: '4px solid',
                    borderColor: 'divider',
                  }}
                >
                  <PersonIcon sx={{ fontSize: 80 }} />
                </Avatar>
              )}
              <MuiTooltip title={employee.isOnline ? t('employees.online') : t('employees.offline')} arrow>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 6,
                    right: 6,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: employee.isOnline ? 'success.main' : 'error.main',
                    border: '3px solid',
                    borderColor: 'background.paper',
                  }}
                />
              </MuiTooltip>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: '0.75rem'
              }}
            >
              {t('employees.name')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary'
                }}
              >
                {employee.name}
              </Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: '0.75rem'
              }}
            >
              {t('employees.email')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {employee.email}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: '0.75rem'
              }}
            >
              {t('employees.branch')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {employee.branch?.name || 'N/A'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: '0.75rem'
              }}
            >
              {t('employees.role')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {employee.role_name !== undefined
                ? employee.role_name === 'GM'
                  ? t('employees.roles.admin')
                  : employee.role_name === 'BM'
                  ? t('employees.roles.branchManager')
                  : employee.role_name === 'Employee'
                  ? t('employees.roles.employee')
                  : employee.role_name
                : 'N/A'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: '0.75rem'
              }}
            >
              {t('employees.salary')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {employee.salary ? `AED ${employee.salary.toLocaleString()}` : 'N/A'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: '0.75rem'
              }}
            >
              {t('employees.availableLeave')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {employee.available_leave}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(2, 139, 203, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: '0.75rem'
              }}
            >
              {t('employees.availableSickLeave')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {employee.available_sick_leave ?? 0}
            </Typography>
          </Paper>
        </Grid>

        {/* Face ID Settings - Only show for own profile */}
        {user && employee && user.id === employee.id && (
          <Grid size={{ xs: 12 }}>
            <FaceIdSettings />
          </Grid>
        )}
      </Grid>
    );
  }, [employee, t, user]);

  const renderStatistics = React.useMemo(() => {
    const isRtl = direction === 'rtl';

    // Show loading state
    if (statisticsLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: 500,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    // Show error state
    if (statisticsError) {
      return (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Alert severity="error">{statisticsError.message}</Alert>
        </Box>
      );
    }

    // Calculate total and percentages
    const totalCount = statistics
      ? statistics.counts.CREATED + statistics.counts.COMPLETED + statistics.counts.BOOKED + statistics.counts.CANCELED
      : 0;

    // If all counts are zero, show empty state
    if (totalCount === 0) {
      return (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {t('employees.requestsStatistics')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {t('common.noData')}
            </Typography>
          </Paper>
        </Box>
      );
    }


    // Calculate percentages
    const pendingPercentage = Math.round((statistics!.counts.CREATED / totalCount) * 100);
    const confirmedPercentage = Math.round(((statistics!.counts.BOOKED + statistics!.counts.COMPLETED) / totalCount) * 100);
    const cancelledPercentage = Math.round((statistics!.counts.CANCELED / totalCount) * 100);

    const data = [
      {
        name: t('employees.pendingRequests'),
        value: pendingPercentage,
        color: '#ff9800'
      },
      {
        name: t('employees.confirmedRequests'),
        value: confirmedPercentage,
        color: '#4caf50'
      },
      {
        name: t('employees.cancelledRequests'),
        value: cancelledPercentage,
        color: '#f44336'
      },
    ];

    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
      const RADIAN = Math.PI / 180;
      // Increase the radius multiplier to add more margin (1.3 means 30% more distance)
      const radius = outerRadius * 1.3;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          fill="#333"
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          style={{ fontSize: '16px', fontWeight: 600 }}
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <Box sx={{ width: '100%', mt: 1 }}>
        {/* Date Range Filter */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
        </Box>

        {/* Total Orders Card */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.08) 0%, rgba(2, 139, 203, 0.03) 100%)',
            border: '2px solid',
            borderColor: '#028bcb',
            borderRadius: 2,
            textAlign: 'center',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(2, 139, 203, 0.2)',
              transform: 'translateY(-4px)',
            }
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 1.5,
              fontSize: '0.85rem',
            }}
          >
            {t('employees.totalOrders')}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mt: 1,
              fontWeight: 700,
              color: '#028bcb',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            {statistics?.total || 0}
          </Typography>
        </Paper>

        {/* Statistics Chart */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.05) 0%, rgba(2, 139, 203, 0.02) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 500,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              fontWeight: 600,
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            {t('employees.requestsStatistics')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => `${value}%`}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  padding: '10px',
                  direction: isRtl ? 'rtl' : 'ltr',
                  fontFamily: isRtl ? 'Cairo, sans-serif' : 'inherit',
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                wrapperStyle={{
                  direction: isRtl ? 'rtl' : 'ltr',
                  fontFamily: isRtl ? 'Cairo, sans-serif' : 'inherit',
                }}
                formatter={(value: string) => (
                  <span style={{ color: '#666', fontWeight: 500 }}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    );
  }, [t, direction, statistics, statisticsLoading, statisticsError, dateFrom, dateTo]);

  const renderShow = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }
    const isRtl = direction === 'rtl';
    return employee ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        {/* Luxury Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.08) 0%, rgba(2, 139, 203, 0.03) 100%)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              px: 2,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(90deg, #028bcb 0%, #0277a8 100%)',
              },
              '& .MuiTabs-scrollButtons': {
                color: '#028bcb',
                transform: isRtl ? 'rotate(180deg)' : 'none',
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
              '& .MuiTab-root': {
                minHeight: 72,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.secondary',
                transition: 'all 0.3s ease',
                margin: '12px 8px',
                padding: '12px 20px',
                borderRadius: 2,
                position: 'relative',
                '&:not(:last-child)::after': {
                  content: '""',
                  position: 'absolute',
                  right: '-8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '1px',
                  height: '40px',
                  background: 'linear-gradient(180deg, transparent 0%, rgba(2, 139, 203, 0.3) 20%, rgba(2, 139, 203, 0.6) 50%, rgba(2, 139, 203, 0.3) 80%, transparent 100%)',
                  boxShadow: '0 0 8px rgba(2, 139, 203, 0.3)',
                },
                '&:hover': {
                  color: '#028bcb',
                  background: 'rgba(2, 139, 203, 0.05)',
                },
                '&.Mui-selected': {
                  color: '#028bcb',
                  fontWeight: 600,
                  background: 'rgba(2, 139, 203, 0.08)',
                },
              },
            }}
          >
            <Tab
              icon={<PersonIcon sx={{ fontSize: 24 }} />}
              iconPosition="start"
              label={t('employees.personalInfo') || 'Personal Information'}
            />
            {user?.role !== 'Employee' && (
              <Tab
                icon={<EventIcon sx={{ fontSize: 24 }} />}
                iconPosition="start"
                label={t('employees.orders') || 'Orders'}
              />
            )}
            {user?.role !== 'Employee' && (
              <Tab
                icon={<PaymentIcon sx={{ fontSize: 24 }} />}
                iconPosition="start"
                label={t('employees.payments') || 'Payments'}
              />
            )}
            {user?.role === 'GM' && (
              <Tab
                icon={<PieChartIcon sx={{ fontSize: 24 }} />}
                iconPosition="start"
                label={t('employees.statistics') || 'Statistics'}
              />
            )}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box
          sx={{
            mb: 4,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {activeTab === 0 && renderPersonalInfo}
          {user?.role !== 'Employee' && activeTab === 1 && employee && (
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              <EmployeeOrdersTable userId={employee.id} />
            </Box>
          )}
          {user?.role !== 'Employee' && activeTab === 2 && employee && (
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              <EmployeeInstallmentsTable userId={employee.id} onDataChange={handleInstallmentDataChange} />
            </Box>
          )}
          {user?.role === 'GM' && activeTab === 3 && renderStatistics}
          {user?.role !== 'Employee' && user?.role !== 'GM' && activeTab === 1 && renderStatistics}
        </Box>

        {/* Action Buttons */}
        {activeTab === 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Stack direction="row" justifyContent="flex-end">
              {employee.contract_photo_path && (
                <RtlButton
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleContractDownload}
                  sx={{
                    background: '#4caf50',
                    boxShadow: 'unset',
                    border: 0,
                    '&:hover': {
                      background: '#45a049',
                    },
                  }}
                >
                  {t('employees.downloadContract') || 'Download Contract'}
                </RtlButton>
              )}

                {user?.role !== 'Employee' && (
                    <RtlButton
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEmployeeEdit}
                        sx={{
                            marginRight: `${isRtl ? 8 : 0}px`,
                            marginLeft: `${isRtl ? 0 : 8}px`,
                            background: '#028bcb',
                            boxShadow: 'unset',
                            border: 0,
                            '&:hover': {
                                background: '#0277a8',
                            },
                        }}
                    >
                        {t('common.edit')}
                    </RtlButton>
                    )}
              {user?.role !== 'Employee' && (
                  <RtlButton
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleEmployeeDelete}
                    sx={{
                      marginRight: `${isRtl ? 8 : 0}px`,
                      marginLeft: `${isRtl ? 0 : 8}px`,
                      '&:hover': { backgroundColor: 'rgb(110,19,19)' },
                    }}
                  >
                    {t('common.delete')}
                  </RtlButton>
              )}
            </Stack>
          </>
        )}
      </Box>
    ) : null;
  }, [
    isLoading,
    error,
    employee,
    activeTab,
    direction,
    handleTabChange,
    renderPersonalInfo,
    renderStatistics,
    handleBack,
    handleEmployeeEdit,
    handleEmployeeDelete,
    handleContractDownload,
    handleInstallmentDataChange,
    t,
    user,
  ]);

  const pageTitle = t('employees.view');
  const isRtl = direction === 'rtl';

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('employees.title'), path: '/employees' },
        { title: pageTitle },
      ]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={{
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: '#31394a',
              color: 'white'
            },
          }}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
